// Page context ONLY — never imported from src/sw/ (the SW bundle stays
// analytics-free, same rule as api/client; see web-sw-boundary-rules). Gated by
// __ANALYTICS_ENABLED__ + the user's analyticsOptOut, both checked before SDK
// construction. Event-spec §3.4.

import { bumpCounter, captureOnce } from "@reflecto/client-core";
import { createAnalytics, type ReflectoAnalytics } from "@reflecto/client-core/analytics";
import type { PaletteCommand } from "@reflecto/client-ui";
import { webStorage } from "../adapters/storage.web";

let instance: ReflectoAnalytics | null = null;

export function getAnalytics(): ReflectoAnalytics {
  if (!instance) {
    instance = createAnalytics({
      surface: "web",
      appVersion: __APP_VERSION__,
      enabled: __ANALYTICS_ENABLED__,
      getOptedOut: async () => (await webStorage.get("analyticsOptOut")).analyticsOptOut === true,
      getDistinctId: async () => (await webStorage.get("deviceId")).deviceId,
    });
  }
  return instance;
}

// Named trackers — the only analytics surface UI code should touch. Fail open, never throw.

async function setPairCodeShownAnchor(): Promise<void> {
  const { analyticsState } = await webStorage.get("analyticsState");
  await webStorage.set({
    analyticsState: { ...(analyticsState ?? {}), pairCodeShownAt: Date.now() },
  });
}

/** Funnel anchor: pairing code on screen + start of the time_to_pair clock. */
export function trackPairingCodeDisplayed(): void {
  getAnalytics().capture("pairing_code_displayed");
  void setPairCodeShownAnchor().catch(() => {});
}

// Consumes the pairCodeShownAt anchor (re-entrant confirms can't double-count),
// aliases this person to the phone (event-spec §1), writes durable pairedAt.
export async function trackPairingCompleted(): Promise<void> {
  try {
    const { analyticsState, androidDeviceId } = await webStorage.get(
      "analyticsState",
      "androidDeviceId"
    );
    const shownAt = analyticsState?.pairCodeShownAt;
    if (shownAt === undefined) return;
    const rest = { ...(analyticsState ?? {}) };
    delete rest.pairCodeShownAt;
    rest.pairedAt = Date.now();
    await webStorage.set({ analyticsState: rest });
    getAnalytics().capture("pairing_completed", {
      time_to_pair_s: Math.round((Date.now() - shownAt) / 1000),
    });
    if (androidDeviceId) getAnalytics().aliasToPhone(androidDeviceId);
  } catch {
    // fail open
  }
}

/** Glance open: recurring intent event + counter + first-view milestone (§4.2). */
export function trackGlanceViewed(): void {
  getAnalytics().capture("glance_viewed");
  void bumpCounter(webStorage, "glance_viewed_count");
  void captureOnce(webStorage, getAnalytics(), "first_glance_viewed");
}

/** Download CTA = the on-device-AI consent gesture (§4.1). */
export function trackGlanceDownloadConsented(): void {
  void captureOnce(webStorage, getAnalytics(), "glance_download_consented");
}

// Seen ≠ delivered (§3.3). pairedAt anchor first; pairAutoNameResult = legacy fallback.
export async function trackNotificationsSeen(count: number): Promise<void> {
  try {
    if (count <= 0) return;
    await bumpCounter(webStorage, "notifications_seen_count", count);
    const { analyticsState, pairAutoNameResult } = await webStorage.get(
      "analyticsState",
      "pairAutoNameResult"
    );
    const pairedAt = analyticsState?.pairedAt ?? pairAutoNameResult?.createdAt;
    await captureOnce(webStorage, getAnalytics(), "first_notification_seen", {
      hours_since_pairing: pairedAt
        ? Math.round(((Date.now() - pairedAt) / 3_600_000) * 10) / 10
        : undefined,
    });
  } catch {
    // fail open
  }
}

// Dismiss/reply choke points in services/messages.ts — intent-only (phone-side
// dismissals ride SSE, never these helpers).
export function trackDismissIntent(count: number): void {
  if (count <= 0) return;
  void bumpCounter(webStorage, "dismissals_count", count);
  void captureOnce(webStorage, getAnalytics(), "first_dismiss");
}

export function trackReplySent(): void {
  void bumpCounter(webStorage, "replies_sent_count");
  void captureOnce(webStorage, getAnalytics(), "first_reply_sent");
}

/**
 * Wraps each palette command's `run` to capture `palette_command_executed`
 * with its static id — `command_id` is the ONLY property (event-spec §4.2),
 * never a label or content. Fail-open: a throwing capture must never block
 * the command itself. The instance is injected (same style as
 * `capturePaletteOpened`) so tests exercise this real helper against a mock.
 */
export function withCommandAnalytics(
  analytics: ReflectoAnalytics,
  commands: PaletteCommand[]
): PaletteCommand[] {
  return commands.map((cmd) => ({
    ...cmd,
    run: () => {
      try {
        analytics.capture("palette_command_executed", { command_id: cmd.id });
      } catch {
        // fail open — analytics must never block a command
      }
      cmd.run();
    },
  }));
}
